import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';

import { TypeInInputBox } from '../../../private/LearnEngineComponents/TypeInInputBox/TypeInInputBox';
import { ISlAnswerProps } from '../../globalInterfaces/learnEngineInterfaces';

import s from './SlTypeInAnswer.scss';
import {
	IQuestionPart, IUIType, IAnswerOption, QuestionPartType, AnswerOptionStatus,
} from '../../../../api/private/learn-engine-api/learn-engine-api.interface';

export function SlTypeInAnswer({
	options,
	questionParts,
	onChange,
	isValid,
	uiType,
	disabled,
}: ISlAnswerProps): JSX.Element {
	useStyles(s);
	const [multiTypeInData, setMultiTypeInData] = useState<IQuestionPart[][]>(null);

	const isCode = uiType === IUIType.Code;

	useEffect(() => {
		const initialOptions: IAnswerOption[] = [];
		if (questionParts) {
			setMultiTypeInData(
				questionParts.reduce(
					(
						data: IQuestionPart[][],
						part: IQuestionPart,
						index: number,
					) => {
						switch (part.type) {
						case QuestionPartType.NewLine:
							data.push([]);
							break;
						case QuestionPartType.Text:
							data[data.length - 1].push(part);
							break;
						case QuestionPartType.InputBox:
							data[data.length - 1].push({
								...part,
								maxLength: part.maxLength,
								optionId: index,
							});
							initialOptions.push({
								id: index,
								text: '',
								orderNumber: index + 1,
								status: AnswerOptionStatus.Initial,
							});
							break;
						default:
							break;
						}
						return data;
					},
					[[]],
				),
			);
		} else {
			initialOptions.push({
				id: 1,
				text: '',
				orderNumber: 1,
				status: AnswerOptionStatus.Initial,
			});
		}
		onChange(initialOptions);
	}, []);

	const onInputChange = (optionId: number, text: string) => {
		const changedOptions: IAnswerOption[] = options.map(
			(option: IAnswerOption) => {
				if (option.id === optionId) {
					return {
						...option,
						text,
						status: text.length
							? AnswerOptionStatus.Selected
							: AnswerOptionStatus.Initial,
					};
				}
				return option;
			},
		);
		isValid(
			!changedOptions.some(
				(opt) => opt.status === AnswerOptionStatus.Initial
                    || !opt.text.trim().length,
			),
		);
		onChange(changedOptions);
	};

	const renderSingleTypeInAnswer = (): JSX.Element => (
		<div
			className="sl-type-in-answer__input-wrapper single"
			sl-test-data="cmpSingleTypeIn"
		>
			<TypeInInputBox
				option={options[0]}
				isCode={isCode}
				maxLength={multiTypeInData?.[0]?.[0]?.maxLength}
				onInputChange={(text: string) => onInputChange(options[0].id, text)}
				disabled={disabled}
				shouldFocus
			/>
		</div>
	);

	const renderMultiTypeInAnswer = (): JSX.Element => (
		<div
			sl-test-data="cmpMultiTypeIn"
			className="sl-type-in-answer--multi"
		>
			{multiTypeInData.map(
				(parts: IQuestionPart[], index: number) => (
					<div className="sl-type-in-answer__row" key={index}>
						{parts.map((part: IQuestionPart, idx) => {
							if (part.type === QuestionPartType.Text) {
								return (
									<div
										key={idx}
										className={classNames(
											'sl-type-in-answer__text',
											{
												code: isCode,
											},
										)}
									>
										{part.content}
									</div>
								);
							}
							if (part.type === QuestionPartType.InputBox) {
								const option = options.find(
									(o: IAnswerOption) => o.id === part.optionId,
								);

								const isFirstInput = part.optionId === options[0]?.id;

								return (
									<div
										className="sl-type-in-answer__input-wrapper multi"
										key={idx}
									>
										<TypeInInputBox
											isCode={isCode}
											shouldFocus={isFirstInput}
											option={option}
											maxLength={part?.maxLength}
											onInputChange={(text: string) => onInputChange(
												option.id,
												text,
											)}
											disabled={disabled}
										/>
									</div>
								);
							}
							return null;
						})}
					</div>
				),
			)}
		</div>
	);

	return options ? (
		<div
			sl-test-data="cmpTypeInAnswer"
			className={classNames('sl-type-in-answer', {
				code: isCode,
			})}
		>
			{multiTypeInData?.length > 1 || multiTypeInData?.[0]?.length > 1
				? renderMultiTypeInAnswer()
				: renderSingleTypeInAnswer()}
		</div>
	) : null;
}
